import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

export type AlertType = 'warning' | 'error' | 'info' | 'success';

export interface Alert {
  id: string;
  type: AlertType;
  title: string;
  message: string;
  actionLabel?: string;
  actionPath?: string;
  count?: number;
  icon?: React.ReactNode;
}

interface AlertCardProps {
  alerts: Alert[];
  maxAlerts?: number;
  className?: string;
}

const alertIcons: Record<AlertType, React.ReactNode> = {
  warning: <AlertCircle className="w-5 h-5 text-yellow-500" />,
  error: <XCircle className="w-5 h-5 text-red-500" />,
  info: <Clock className="w-5 h-5 text-yellow-500" />,
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
};

const alertColors: Record<AlertType, string> = {
  warning: 'border-yellow-500/30 bg-yellow-500/10',
  error: 'border-red-500/30 bg-red-500/10',
  info: 'border-yellow-500/30 bg-yellow-500/10',
  success: 'border-green-500/30 bg-green-500/10',
};

export function AlertCard({ alerts, maxAlerts = 5, className = '' }: AlertCardProps) {
  const navigate = useNavigate();
  const displayedAlerts = alerts.slice(0, maxAlerts);

  if (alerts.length === 0) {
    return (
      <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
        <CardHeader>
          <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            All Clear
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-white/80 text-sm">No issues requiring your attention.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader>
        <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-rollops-accent" />
          Attention Required
          {alerts.length > maxAlerts && (
            <span className="ml-auto text-sm font-normal text-white/80">
              +{alerts.length - maxAlerts} more
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="!p-6 space-y-3">
        {displayedAlerts.map((alert) => (
          <div
            key={alert.id}
            className={`
              p-4 rounded-lg border transition-all duration-200
              ${alertColors[alert.type]}
              ${alert.actionPath ? 'cursor-pointer hover:opacity-80' : ''}
            `}
            onClick={() => {
              if (alert.actionPath) {
                navigate(alert.actionPath);
              }
            }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {alert.icon || alertIcons[alert.type]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-[#EBFFEE] text-sm">{alert.title}</h4>
                  {alert.count !== undefined && alert.count > 1 && (
                    <span className="px-2 py-0.5 bg-[#363232] rounded text-xs text-white/80">
                      {alert.count}
                    </span>
                  )}
                </div>
                <p className="text-sm text-white/80 mb-2">{alert.message}</p>
                {alert.actionLabel && alert.actionPath && (
                  <button
                    className="text-xs font-medium text-rollops-accent hover:text-[#EBFFEE] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (alert.actionPath) {
                        navigate(alert.actionPath);
                      }
                    }}
                  >
                    {alert.actionLabel} →
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
